import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Eye,
  Pencil,
  Trash2,
  XCircle,
  RefreshCw,
  Download,
  Upload,
  Copy,
  MoreHorizontal,
  Plus,
  Check,
  X,
  Search,
  Filter,
  Send,
  Settings,
  ExternalLink,
  Share2,
  Printer,
} from 'lucide-react';
import styles from './IconButton.module.scss';

/* ────────────────────────────────────────────────────────────
 * IconButton — square icon-only action button.
 *
 *   <IconButton icon={<Eye />} intent="info" tooltip="View" />
 *
 * Or use the pre-configured exports below
 * (ViewIconButton, EditIconButton, DeleteIconButton, …) — each
 * ships an opinionated icon + intent + default aria-label so a
 * caller only has to wire onClick.
 * ──────────────────────────────────────────────────────────── */

const IconButton = forwardRef(function IconButton(
  {
    icon,
    intent,
    tone,
    variant,
    size,
    pill,
    loading,
    disabled,
    tooltip,
    'aria-label': ariaLabel,
    className,
    type,
    ...rest
  },
  ref
) {
  // `tone` is the unified color-prop name (matches Modal sub-primitives
  // and Badge's `tone` alias). `intent` is the original IconButton prop
  // and still works; `tone` wins when both are set. See
  // PROMPT_TEMPLATE.md §4 d2 prop-unification note.
  const colorTone = tone || intent;

  const classNames = [
    styles.button,
    styles[variant],
    styles[colorTone],
    styles[size],
    pill ? styles.pill : '',
    loading ? styles.loading : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type={type}
      className={classNames}
      disabled={disabled || loading}
      title={tooltip || undefined}
      aria-label={ariaLabel || tooltip || undefined}
      {...rest}
    >
      {loading ? <span className={styles.spinner} /> : icon}
    </button>
  );
});

const TONE_VALUES = [
  'primary',
  'neutral',
  'success',
  'warning',
  'danger',
  'info',
];

IconButton.propTypes = {
  icon: PropTypes.node,
  // `tone` — unified color-prop name (preferred for new code).
  tone: PropTypes.oneOf(TONE_VALUES),
  // `intent` — original prop name, still supported.
  intent: PropTypes.oneOf(TONE_VALUES),
  variant: PropTypes.oneOf(['solid', 'soft', 'ghost', 'outline']),
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  pill: PropTypes.bool,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  tooltip: PropTypes.string,
  'aria-label': PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.string,
};

IconButton.defaultProps = {
  icon: null,
  tone: undefined,
  intent: 'neutral',
  variant: 'soft',
  size: 'sm',
  pill: false,
  loading: false,
  disabled: false,
  tooltip: '',
  'aria-label': '',
  className: '',
  type: 'button',
};

/* ── Pre-configured convenience buttons ─────────────────────
 * Each one fixes the icon + intent + default tooltip so the
 * call site is just `<ViewIconButton onClick={…} />`.
 * Any prop (size, variant, tooltip, className) can be overridden.
 * ──────────────────────────────────────────────────────────── */

const makePreset = (Icon, defaults) => {
  const Preset = forwardRef((props, ref) => (
    <IconButton
      ref={ref}
      {...defaults}
      {...props}
      icon={props.icon || <Icon />}
    />
  ));
  Preset.displayName = defaults.displayName || 'PresetIconButton';
  return Preset;
};

export const ViewIconButton = makePreset(Eye, {
  intent: 'info',
  tooltip: 'View',
  displayName: 'ViewIconButton',
});

export const EditIconButton = makePreset(Pencil, {
  intent: 'primary',
  tooltip: 'Edit',
  displayName: 'EditIconButton',
});

export const DeleteIconButton = makePreset(Trash2, {
  intent: 'danger',
  tooltip: 'Delete',
  displayName: 'DeleteIconButton',
});

export const CancelIconButton = makePreset(XCircle, {
  intent: 'danger',
  tooltip: 'Cancel',
  displayName: 'CancelIconButton',
});

export const RefreshIconButton = makePreset(RefreshCw, {
  intent: 'neutral',
  tooltip: 'Refresh',
  displayName: 'RefreshIconButton',
});

export const DownloadIconButton = makePreset(Download, {
  intent: 'primary',
  tooltip: 'Download',
  displayName: 'DownloadIconButton',
});

export const UploadIconButton = makePreset(Upload, {
  intent: 'primary',
  tooltip: 'Upload',
  displayName: 'UploadIconButton',
});

export const CopyIconButton = makePreset(Copy, {
  intent: 'neutral',
  tooltip: 'Copy',
  displayName: 'CopyIconButton',
});

export const MoreIconButton = makePreset(MoreHorizontal, {
  intent: 'neutral',
  variant: 'ghost',
  tooltip: 'More',
  displayName: 'MoreIconButton',
});

export const AddIconButton = makePreset(Plus, {
  intent: 'primary',
  tooltip: 'Add',
  displayName: 'AddIconButton',
});

export const ApproveIconButton = makePreset(Check, {
  intent: 'success',
  tooltip: 'Approve',
  displayName: 'ApproveIconButton',
});

export const CloseIconButton = makePreset(X, {
  intent: 'neutral',
  variant: 'ghost',
  tooltip: 'Close',
  displayName: 'CloseIconButton',
});

export const SearchIconButton = makePreset(Search, {
  intent: 'neutral',
  tooltip: 'Search',
  displayName: 'SearchIconButton',
});

export const FilterIconButton = makePreset(Filter, {
  intent: 'neutral',
  tooltip: 'Filter',
  displayName: 'FilterIconButton',
});

export const SendIconButton = makePreset(Send, {
  intent: 'primary',
  tooltip: 'Send',
  displayName: 'SendIconButton',
});

export const SettingsIconButton = makePreset(Settings, {
  intent: 'neutral',
  tooltip: 'Settings',
  displayName: 'SettingsIconButton',
});

export const ExternalLinkIconButton = makePreset(ExternalLink, {
  intent: 'primary',
  variant: 'ghost',
  tooltip: 'Open',
  displayName: 'ExternalLinkIconButton',
});

export const ShareIconButton = makePreset(Share2, {
  intent: 'primary',
  tooltip: 'Share',
  displayName: 'ShareIconButton',
});

export const PrintIconButton = makePreset(Printer, {
  intent: 'primary',
  tooltip: 'Print',
  displayName: 'PrintIconButton',
});

export default IconButton;
